/**
 * drilldownApi — every fetch the two-tier drill-down drawer makes.
 *
 * Rules (inherited from person/personApi.js §2.9):
 *   - Read-only. No writes of any kind.
 *   - Failures THROW. Never swallow an error into a silent empty state; an
 *     empty drawer and a failed drawer must not look identical.
 *
 * DUAL SOURCE: the person endpoints (`brain-person/*`) and both drill-down
 * endpoints (`projects/:id/member-hours`, `analytics/member/:id/projects`) all
 * return app + background-agent time, with a `sources` block explaining the
 * split. This module never merges sources client-side — the server owns the
 * "app wins on the same day/project" rule so the two can't drift apart.
 */

import { apiInstance as api } from "../../../../hooks/useApi";
import { SERVER_URL } from "../../../../utils/serverConfig";
import { fetchDossier, fetchRead } from "../../screens/brain/person/personApi";

/** Local-date string. toISOString() would shift the day for UTC+ users. */
export const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

function toError(err, fallback) {
  const payload = err && err.response && err.response.data;
  const inner = payload && payload.error;
  const e = new Error(
    (inner && inner.message) || (payload && payload.message) || (err && err.message) || fallback
  );
  e.code = (inner && inner.code) || "INTERNAL";
  e.status = (err && err.response && err.response.status) || 0;
  return e;
}

function unwrap(res, fallback) {
  const payload = res && res.data;
  if (!payload || payload.success === false) {
    const inner = payload && payload.error;
    const e = new Error((inner && inner.message) || fallback);
    e.code = (inner && inner.code) || "INTERNAL";
    throw e;
  }
  return payload.data || {};
}

/* ── PERSON ──────────────────────────────────────────────────────────────── */

/** Deterministic spine: metrics (incl. `sources`), projects (incl. `source`), timeline, signals. */
export function personDossier(userId, range, opts) {
  return fetchDossier(
    userId,
    {
      start: range?.start ? new Date(range.start).toISOString() : undefined,
      end: range?.end ? new Date(range.end).toISOString() : undefined,
    },
    opts
  );
}

/** The AI read — 3 sentences + tone. Source-aware since dossier 1.2.0. */
export function personRead(userId, range, opts) {
  return fetchRead(
    userId,
    {
      start: range?.start ? new Date(range.start).toISOString() : undefined,
      end: range?.end ? new Date(range.end).toISOString() : undefined,
    },
    opts
  );
}

/**
 * What the person actually WORKED ON — the written record, not the meters.
 *
 * Deliberately a separate request from the dossier: the dossier answers "how
 * much, when, how idle", this answers "on what". It is deterministic and cached
 * server-side, so opening the drawer never waits on a model.
 *
 * Fails soft at the call site (the drawer keeps its metrics if this 500s) but
 * still THROWS here, per the module rule — an empty log and a broken log must
 * not look the same.
 */
export async function personWorklog(userId, range, opts) {
  const params = new URLSearchParams();
  if (range?.start) params.set("start", fmtDate(range.start));
  if (range?.end) params.set("end", fmtDate(range.end));
  /* Project scope — "what did this person do ON THIS PROJECT". Sent by name
     because the written narrative carries a project name, never an id. */
  if (opts?.project) params.set("project", String(opts.project));
  if (opts?.refresh) params.set("refresh", "1");
  try {
    const res = await api.get(
      `/api/v1/brain-person/${encodeURIComponent(userId)}/worklog?${params.toString()}`,
      opts?.signal ? { signal: opts.signal } : undefined
    );
    return unwrap(res, "Could not load this person's work log.");
  } catch (err) {
    throw toError(err, "Could not load this person's work log.");
  }
}

/** Level 2 — the member's project split, app + agent. */
export async function personProjects(userId, range, opts) {
  const params = new URLSearchParams({
    startDate: fmtDate(range?.start),
    endDate: fmtDate(range?.end),
  });
  try {
    const res = await api.get(
      `/api/v1/analytics/member/${encodeURIComponent(userId)}/projects?${params.toString()}`,
      opts?.signal ? { signal: opts.signal } : undefined
    );
    return unwrap(res, "Could not load this member's projects.");
  } catch (err) {
    throw toError(err, "Could not load this member's projects.");
  }
}

/* ── PROJECT ─────────────────────────────────────────────────────────────── */

/** The project roster + totals, app + agent (agent-only contributors included). */
export async function projectMembers(projectId, range, opts) {
  const params = new URLSearchParams({
    startDate: fmtDate(range?.start),
    endDate: fmtDate(range?.end),
  });
  try {
    const res = await api.get(
      `/api/v1/projects/${encodeURIComponent(projectId)}/member-hours?${params.toString()}`,
      opts?.signal ? { signal: opts.signal } : undefined
    );
    return unwrap(res, "Could not load this project's members.");
  } catch (err) {
    throw toError(err, "Could not load this project's members.");
  }
}

/* ── Derivations shared by the drawer ────────────────────────────────────── */

export function memberName(m) {
  return `${m?.firstName || ""} ${m?.lastName || ""}`.trim() || m?.email || "Member";
}

/**
 * Project takeaways, computed in code from real figures — no model call.
 *
 * The person side gets its takeaways from the server's deterministic
 * `signals[]` plus an AI `read`. There is no project equivalent yet, so these
 * are derived here from the same numbers the drawer already displays. Every
 * one of them quotes a figure that is on screen; none is an inference.
 */
export function projectTakeaways(data) {
  const totals = data?.projectTotals || {};
  const members = data?.memberHours || [];
  const sources = totals.sources || null;
  const out = [];

  const totalMinutes = Math.round((Number(totals.totalHours) || 0) * 60);
  const idleMinutes = Number(totals.totalIdleTimeMinutes) || 0;
  const idleRatio = totalMinutes > 0 ? idleMinutes / totalMinutes : 0;

  if (sources && sources.agent?.minutes > 0) {
    const share = totalMinutes > 0 ? Math.round((sources.agent.minutes / totalMinutes) * 100) : 0;
    out.push({
      sev: sources.primary === "agent" ? "info" : "info",
      title: sources.primary === "agent"
        ? "No session detail for this project"
        : `${share}% of this project has no session detail`,
      detail: sources.primary === "agent"
        ? `${sources.agent.formatted} was tracked without start/stop sessions, so session-level views are empty for it.`
        : `${sources.agent.formatted} of ${totals.totalHours ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m` : "0m"} carries no session detail.`,
    });
  }

  const top = members[0];
  if (top && totalMinutes > 0) {
    const share = Math.round(((Number(top.totalHours) || 0) * 60 / totalMinutes) * 100);
    if (share >= 50 && members.length > 1) {
      out.push({
        sev: "warning",
        title: `${memberName(top)} carries ${share}% of it`,
        detail: `${top.totalHours}h of the project's time is one person; the next contributor is at ${members[1]?.totalHours || 0}h.`,
      });
    } else if (members.length === 1) {
      out.push({
        sev: "warning",
        title: "Single-contributor project",
        detail: `${memberName(top)} is the only person with tracked time on this project in the window.`,
      });
    } else {
      out.push({
        sev: "info",
        title: `${memberName(top)} is the largest contributor`,
        detail: `${top.totalHours}h of ${Math.round((Number(totals.totalHours) || 0) * 10) / 10}h, ${share}% of the project.`,
      });
    }
  }

  if (idleRatio >= 0.22 && idleMinutes >= 60) {
    out.push({
      sev: idleRatio >= 0.4 ? "negative" : "warning",
      title: `Idle is ${Math.round(idleRatio * 100)}% of tracked time`,
      detail: `${totals.totalIdleTimeFormatted || `${idleMinutes}m`} idle against ${totals.totalActiveTimeFormatted || "0h 0m"} active.`,
    });
  }

  if (sources && sources.sessionsCoverAllTime === false) {
    out.push({
      sev: "warning",
      title: "Session counts understate the work",
      detail: "Part of this project's time carries no session detail, so session totals and average session cover only a share of it.",
    });
  }

  if (!out.length) {
    out.push({
      sev: "info",
      title: `${members.length} ${members.length === 1 ? "person" : "people"} tracked time`,
      detail: `${totals.totalActiveTimeFormatted || "0h 0m"} active across ${totals.totalSessions || 0} sessions.`,
    });
  }

  return out.slice(0, 5);
}

/** Deterministic 3-sentence project read, same shape as the person `read`. */
export function projectRead(data, projectName) {
  const totals = data?.projectTotals || {};
  const members = data?.memberHours || [];
  const sources = totals.sources || null;
  const totalMinutes = Math.round((Number(totals.totalHours) || 0) * 60);
  const fmt = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;

  if (!totalMinutes) {
    return [
      `${projectName} has no tracked time in this period.`,
      "No one on the roster recorded work against it.",
      "Either tracking was off, or the work was attributed elsewhere.",
    ];
  }

  const top = members[0];
  const third = sources && sources.primary === "agent"
    ? "None of this window was captured as start/stop sessions, so session-level detail is unavailable."
    : sources && sources.agent?.minutes > 0
      ? `${sources.agent.formatted} of it carries no session detail.`
      : `Idle ran at ${totalMinutes > 0 ? Math.round(((Number(totals.totalIdleTimeMinutes) || 0) / totalMinutes) * 100) : 0}% of tracked time.`;

  return [
    `${projectName} absorbed ${fmt(totalMinutes)} across ${members.length} ${members.length === 1 ? "contributor" : "contributors"}.`,
    top
      ? `${memberName(top)} carried the largest share at ${top.totalHours}h.`
      : "No single contributor dominates the window.",
    third,
  ];
}

/* ── ORG BRIEF — the "Know your organization" drawer ─────────────────────
   Server-side: scope-first facts + analysed-layer grounding; the model only
   narrates and every figure beside the prose is server-computed. Cached ~30m
   per (scope, range); pass refresh to re-write. */
export async function orgBrief(range, opts) {
  const params = new URLSearchParams();
  if (range?.start) params.set("startDate", fmtDate(range.start));
  if (range?.end) params.set("endDate", fmtDate(range.end));
  // No timezone param — the server buckets in the ORG timezone (the clock the
  // stored day-rolls already use), so every viewer reads the same brief.
  if (opts?.member) params.set("userId", String(opts.member)); // member dropdown scope
  if (opts?.refresh) params.set("refresh", "1");
  try {
    const res = await api.get(
      `/api/v1/classic-dashboard/org-brief?${params.toString()}`,
      opts?.signal ? { signal: opts.signal } : undefined
    );
    return unwrap(res, "The brief could not be written right now.");
  } catch (err) {
    throw toError(err, "The brief could not be written right now.");
  }
}

/** Cached alerts only — no write. Empty if the stream has not landed yet. */
export async function orgAlertsGet(range, opts) {
  const params = new URLSearchParams();
  if (range?.start) params.set("startDate", fmtDate(range.start));
  if (range?.end) params.set("endDate", fmtDate(range.end));
  if (opts?.member) params.set("userId", String(opts.member));
  try {
    const res = await api.get(
      `/api/v1/classic-dashboard/org-alerts?${params.toString()}`,
      opts?.signal ? { signal: opts.signal } : undefined
    );
    return unwrap(res, "Alerts could not be read right now.");
  } catch (err) {
    throw toError(err, "Alerts could not be read right now.");
  }
}

/** Admin alerts — SSE stream of named work / blockers. Redis replay. */
export async function orgAlertsStream(range, opts, onEvent) {
  return readAlertStream(range, opts, onEvent);
}

/** Today's news desk — separate Haiku write, same SSE shape. */
export async function orgNewsStream(range, opts, onEvent) {
  return readAlertStream(range, { ...opts, desk: "news" }, onEvent);
}

async function readAlertStream(range, opts, onEvent) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${SERVER_URL}/api/v1/classic-dashboard/org-alerts-stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      startDate: range?.start ? fmtDate(range.start) : undefined,
      endDate: range?.end ? fmtDate(range.end) : undefined,
      userId: opts?.member || undefined,
      desk: opts?.desk || undefined,
    }),
    signal: opts?.signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (err?.error?.code === "AI_LIMIT_REACHED") throw new Error("AI limit reached for this organisation.");
    throw toError({ response: { data: err, status: res.status } }, "Alerts could not be written right now.");
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() || "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      let d;
      try { d = JSON.parse(line.slice(6)); } catch (_e) { continue; }
      onEvent(d);
    }
  }
}
